import React from 'react'
import styled from 'styled-components'
import {
  useSettingsFinance,
  useFinanceOnboarding,
  useFinanceDashboard,
} from '@/v2/hooks/settings/useSettingsFinance'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { SettingsPage } from '@/v2/components/settings/shared'
import LoaderGif from '#/assets/images/gif/loader.gif'

const BlankState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
`

const Icon = styled.div`
  width: 4rem;
  height: 4rem;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: #9ca3af;
`

const BlankStateTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2rem;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
`

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  font-size: 0.875rem;
  width: 16rem;
  justify-content: center;
  text-decoration: none;
  background-color: #2563eb;
  color: white;
  border-color: #2563eb;

  &:hover {
    background-color: #1d4ed8;
    text-decoration: none;
  }
`

const Loader = styled.img`
  width: 32px;
  margin: 0 auto 8px;
`

const VerificationText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  max-width: 32rem;
  margin: 0 auto;
`

const Finance = () => {
  const { finance: financeData, isLoading: loading } = useSettingsFinance()
  const {
    startOnboarding,
    isPending: onboardingPending,
  } = useFinanceOnboarding()
  const { openDashboard, isPending: dashboardPending } = useFinanceDashboard()
  const { onOpenSnackbar } = useSnackbarContext()

  const actionLoading = onboardingPending || dashboardPending

  const handleOnboarding = async () => {
    try {
      const data = await startOnboarding()
      if (data.url) {
        window.location.href = data.url
      }
    } catch (error) {
      onOpenSnackbar('Error starting onboarding. Please try again.', 'error')
    }
  }

  const handleAccessDashboard = async () => {
    try {
      const data = await openDashboard()
      if (data.url) {
        window.location.href = data.url
      }
    } catch (error) {
      onOpenSnackbar('Error accessing dashboard. Please try again.', 'error')
    }
  }

  const onboardingStatus = financeData?.onboardingStatus
  const hasLedger = financeData?.hasLedger
  const isCompleted = onboardingStatus === 'completed'
  const isPendingVerification = onboardingStatus === 'pending_verification'
  const isIncomplete =
    onboardingStatus === 'incomplete' || onboardingStatus === 'pending'
  const isNotStarted = !hasLedger

  return (
    <SettingsPage
      title="Finance"
      subtitle="Manage financial settings and payment processing"
      loading={loading}
    >
      <BlankState>
        <Icon>
          <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
          </svg>
        </Icon>

        <BlankStateTitle>
          Clearworks partners with Stripe for secure financial services.
        </BlankStateTitle>

        {isCompleted && (
          <Button onClick={handleAccessDashboard} disabled={actionLoading}>
            {actionLoading ? 'Loading...' : 'Access Stripe dashboard'}
          </Button>
        )}

        {isPendingVerification && (
          <>
            <Loader src={LoaderGif} alt="Loading" />
            <VerificationText>
              Stripe is verifying your account. Please wait a few minutes and{' '}
              <strong>refresh this page</strong> to check if verification has
              been completed.
            </VerificationText>
          </>
        )}

        {isIncomplete && (
          <Button onClick={handleOnboarding} disabled={actionLoading}>
            {actionLoading ? 'Loading...' : 'Set up Stripe account'}
          </Button>
        )}

        {isNotStarted &&
          !isIncomplete &&
          !isPendingVerification &&
          !isCompleted && (
            <Button onClick={handleOnboarding} disabled={actionLoading}>
              {actionLoading ? 'Loading...' : 'Set up Stripe account'}
            </Button>
          )}
      </BlankState>
    </SettingsPage>
  )
}

export default Finance
